class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
    this.setRecommendedProductAccessibility();
  }

  /* =========================================
     HEADER CART ICON
  ========================================= */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();

      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();

        this.open(cartLink);
      }
    });
  }

  /* =========================================
     RECOMMENDED PRODUCT BUTTONS
  ========================================= */

  setRecommendedProductAccessibility() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-al-cart-add]');

      if (!button) return;

      if (button.disabled) return;

      if (button.classList.contains('is-loading')) return;

      event.preventDefault();

      this.addRecommendedProduct(button);
    });
  }

  /* =========================================
     ADD RECOMMENDED PRODUCT
  ========================================= */

  async addRecommendedProduct(button) {
    const variantId = button.dataset.variantId;

    if (!variantId) return;

    const originalText = button.textContent;

    button.classList.add('is-loading');
    button.disabled = true;
    button.textContent = 'Adding...';

    const formData = new FormData();

    formData.append('id', variantId);
    formData.append('quantity', '1');

    formData.append(
      'sections',
      'cart-drawer,cart-icon-bubble'
    );

    formData.append(
      'sections_url',
      window.location.pathname
    );

    const addUrl =
      window.routes && window.routes.cart_add_url
        ? window.routes.cart_add_url
        : '/cart/add.js';

    try {
      const response = await fetch(addUrl, {
        method: 'POST',

        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },

        body: formData,
      });

      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(
          parsedState.description ||
            'Unable to add this product.'
        );
      }

      this.renderContents(parsedState);
    } catch (error) {
      console.error(
        'ALANNA cart recommendation error:',
        error
      );

      button.classList.remove('is-loading');

      button.disabled = false;

      button.textContent = originalText;

      const errorBox = this.querySelector(
        '#CartDrawer-CartErrors'
      );

      if (errorBox) {
        errorBox.textContent =
          error.message ||
          'Unable to add this product. Please try again.';
      }
    }
  }

  /* =========================================
     OPEN CART DRAWER
  ========================================= */

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      {
        once: true,
      }
    );

    document.body.classList.add(
      'overflow-hidden'
    );

    const cartDrawerItems =
      this.querySelector('cart-drawer-items');

    if (
      cartDrawerItems &&
      typeof cartDrawerItems.dispatchViewEvent ===
        'function'
    ) {
      cartDrawerItems.dispatchViewEvent();
    }
  }

  /* =========================================
     CLOSE CART DRAWER
  ========================================= */

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }

  /* =========================================
     CART NOTE ACCESSIBILITY
  ========================================= */

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    const controlledElement =
      cartDrawerNote.nextElementSibling;

    if (
      controlledElement &&
      controlledElement.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        controlledElement.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest('details');

        if (!details) return;

        event.currentTarget.setAttribute(
          'aria-expanded',
          !details.hasAttribute('open')
        );
      }
    );

    if (cartDrawerNote.parentElement) {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  /* =========================================
     UPDATE DRAWER AFTER ADD TO CART
  ========================================= */

  renderContents(parsedState) {
    if (!parsedState) return;

    this.productId = parsedState.id;

    const drawerInner =
      this.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.remove('is-empty');
    }

    this.classList.remove('is-empty');

    const sections =
      this.getSectionsToRender();

    sections.forEach((section) => {
      if (!parsedState.sections) return;

      if (!parsedState.sections[section.id]) {
        return;
      }

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const newHTML =
        this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );

      if (newHTML !== null) {
        sectionElement.innerHTML = newHTML;
      }
    });

    setTimeout(() => {
      const overlay =
        this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      this.open();
    }, 0);
  }

  /* =========================================
     GET SECTION HTML
  ========================================= */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return '';

    const documentHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      documentHTML.querySelector(selector);

    return element
      ? element.innerHTML
      : '';
  }

  /* =========================================
     CART DRAWER AJAX SECTIONS
  ========================================= */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },

      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

/* =========================================
   REGISTER CART DRAWER
========================================= */

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

/* =========================================
   CART DRAWER ITEMS
========================================= */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      /* Main cart contents */

      {
        id: 'CartDrawer',

        section: 'cart-drawer',

        selector: '.drawer__inner',
      },

      /* Recommended Best Seller panel */

      {
        id: 'AlCartRecommendationsShell',

        section: 'cart-drawer',

        selector:
          '#AlCartRecommendationsShell',
      },

      /* Header cart count */

      {
        id: 'cart-icon-bubble',

        section: 'cart-icon-bubble',

        selector: '.shopify-section',
      },
    ];
  }
}

/* =========================================
   REGISTER CART DRAWER ITEMS
========================================= */

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}